import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../components/Alert';
import { EmailDeliveryGuidance } from '../../components/EmailDeliveryGuidance';
import { authApi } from '../../features/auth/auth.api';
import { toDisplayMessage } from '../../lib/error-message';
import { paths } from '../../routes/paths';

import styles from './AuthForm.module.css';
import { AuthFormField } from './AuthFormField';
import { AuthLayout } from './AuthLayout';

/**
 * Requests a fresh verification link.
 *
 * Enumeration-safe: the backend always returns the same response whether the
 * address is unknown, already verified, or pending verification.
 */
export function ResendVerificationPage(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await authApi.resendVerification(email);
      setSubmitted(true);
    } catch (caught: unknown) {
      setError(toDisplayMessage(caught, 'Unable to send the email. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <AuthLayout
        title="Check your email"
        description="If an account requires verification, we've sent a new verification link."
        footer={
          <p>
            <Link to={paths.login}>Back to sign in</Link>
          </p>
        }
      >
        <Alert tone="success">
          Verification email sent. Please check your inbox.
        </Alert>
        <EmailDeliveryGuidance />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Resend verification email"
      description="Enter the email address you used to create your Seamontech account."
      heroHeadline="Secure access to your Seamontech account."
      heroLead="We'll send a fresh link so you can activate your account."
      footer={
        <p>
          <Link to={paths.login}>Back to sign in</Link>
        </p>
      }
    >
      <form className={styles.form} onSubmit={(event) => void handleSubmit(event)} noValidate>
        {error !== null && <Alert tone="error">{error}</Alert>}

        <AuthFormField
          label="Email address"
          inputType="email"
          name="email"
          icon="mail"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          autoComplete="email"
          placeholder="you@example.com"
          required
        />

        <button className={styles.submitButton} type="submit" disabled={busy} aria-busy={busy}>
          {busy ? 'Sending…' : 'Send verification email'}
          {!busy && (
            <span className={styles.submitArrow} aria-hidden="true">
              →
            </span>
          )}
        </button>
      </form>
    </AuthLayout>
  );
}
